import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Divider, Stack, Typography, Button, Dialog } from "@mui/material";

import CustomDropdown from "components/common/Dropdown";
import Daterange from "components/common/Daterange";
import RadioButton from "components/common/RadioButtons";
import AttachDocuments from "components/Screens/Timeoff/ManageTimeoff/RequestTimeoff/AttachDocuments";

import { showSnackbar } from "store/actions/Utility";
import {
  addEmployeeNewLeaveRequest,
  addEmployeeNewLeaveRequestV4,
  getTotalLeaveCount,
  getTotalLeaveCountV2,
} from "./TimeOffAdminServices/EmployeeTimeOffDetailsService";
import { editLeaveRequest, uploadMedicalDocs } from "services/useTimeoffService";

import ArrowIcons from "./EmployeeTimeOffDetails/Icons/ArrowIcons";

const dropdownStyles = {
  borderRadius: "33px",
  border: "1px solid var(--color-primitives-primary-100)",

  minWidth: "auto",
  display: "flex",
  fontFamily: "Poppins-Medium",
  fontWeight: 600,
};

function EditLeavesTaken({
  open,
  closeModal,
  employeeLeavesData,
  onSuccess,
  allLeavesAndHolidaysData,
  leaveToEdit = null,
}) {
  const dispatch = useDispatch();
  const { anniversaryBasedTimeOff, futureCycleLeavesFiling } = useFlags();

  const isEditMode = !!leaveToEdit;

  const [leaveTypeDropdownOptions, setLeaveTypeDropdownOptions] = useState([]);
  const [, setAddNewLeaveRequestLoader] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [totalLeaveCount, setTotalLeaveCount] = useState(0);
  const [addLeaveRequest, setAddLeaveRequest] = useState({
    startDate: isEditMode ? moment(leaveToEdit.dateFrom) : moment(),
    endDate: isEditMode ? moment(leaveToEdit.dateTo) : moment(),
    leaveTypeId: isEditMode ? leaveToEdit.leaveTypeId : 1,
    reason: isEditMode ? leaveToEdit.description || "" : null,
  });
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [dayDuration, setDayDuration] = useState(isEditMode ? leaveToEdit.duration || "Full Day" : "Full Day");

  // Stable reference so Daterange's reset effect does not fire on every render
  // (which would snap the calendar back to the original dates while editing).
  const editInitialDateRange = useMemo(
    () => (isEditMode ? { startDate: leaveToEdit.dateFrom, endDate: leaveToEdit.dateTo } : null),
    [isEditMode, leaveToEdit?.dateFrom, leaveToEdit?.dateTo]
  );
  const [canUserUploadDocuments, setCanUserUploadDocuments] = useState(
    !anniversaryBasedTimeOff &&
      (anniversaryBasedTimeOff
        ? selectedLeaveDetails?.additionalDocumentsRequired
        : selectedLeaveDetails?.attachDocument)
  );
  const [selectedLeaveDetails, setSelectedLeaveDetails] = useState(null);
  const getEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.getEmployeeDetails);

  // eslint-disable-next-line consistent-return
  const addNewLeaveRequest = async () => {
    setAddNewLeaveRequestLoader(true);

    if (!isEditMode && canUserUploadDocuments && filesToUpload.length === 0) {
      return dispatch(showSnackbar({ dispatch, type: "error", message: "Upload doctor's note to request sick leave" }));
    }

    try {
      if (isEditMode) {
        const res = await editLeaveRequest({
          requestId: leaveToEdit.requestId,
          employeeId: leaveToEdit.employeeId,
          selectedDateRange: { startDate: addLeaveRequest?.startDate, endDate: addLeaveRequest?.endDate },
          selectedLeaveType: leaveToEdit.leaveTypeId,
          description: addLeaveRequest?.reason,
          duration: anniversaryBasedTimeOff ? dayDuration : undefined,
        });

        setAddNewLeaveRequestLoader(false);
        if (res?.ok && res?.payload?.success) {
          closeModal();
          dispatch(showSnackbar({ dispatch, type: "success", message: "Leave updated successfully!" }));
          if (onSuccess) onSuccess();
        } else {
          dispatch(
            showSnackbar({
              dispatch,
              type: "error",
              message:
                res?.payload?.message || res?.payload?.error?.[0]?.description || "Failed to update leave request",
            })
          );
        }
        // eslint-disable-next-line consistent-return
        return;
      }

      const payload = {
        dateFrom: moment(addLeaveRequest?.startDate).format("YYYY-MM-DD"),
        dateTo: moment(addLeaveRequest?.endDate).format("YYYY-MM-DD"),
        noOfLeaves: totalLeaveCount,
        description: addLeaveRequest?.reason,
        customerId: getEmployeeDetails?.payload?.customerId,
        employeeId: getEmployeeDetails?.payload?.employeeId,
        leaveTypeId: selectedLeaveDetails?.leaveTypeId,
      };

      if (anniversaryBasedTimeOff && dayDuration) {
        payload.duration = dayDuration;
      }

      if (anniversaryBasedTimeOff) {
        payload.documents = filesToUpload.map((file) => ({
          ...file,
          extension: `.${file?.fileName?.split(".")?.pop()}`,
        }));
      }

      const res = futureCycleLeavesFiling
        ? await addEmployeeNewLeaveRequestV4(payload)
        : await addEmployeeNewLeaveRequest(payload);

      if (res?.success) {
        if (!anniversaryBasedTimeOff) {
          await uploadMedicalDocs(res?.payload?.requestId, filesToUpload);
        }

        setAddNewLeaveRequestLoader(false);
        closeModal();
        dispatch(showSnackbar({ dispatch, type: "success", message: "Leave Created Successfully!" }));
        if (onSuccess) onSuccess();
      } else {
        dispatch(showSnackbar({ dispatch, type: "error", message: res?.error?.[0].description }));
      }
    } catch (error) {
      setAddNewLeaveRequestLoader(false);
    }
  };

  // eslint-disable-next-line consistent-return
  const fetchTotalLeaveCount = async () => {
    try {
      const startDate = moment(addLeaveRequest?.startDate).format("YYYY-MM-DD");
      const endDate = moment(addLeaveRequest?.endDate).format("YYYY-MM-DD");

      const excludeRequestId = isEditMode ? leaveToEdit.requestId : null;
      const countEmployeeId = isEditMode ? leaveToEdit.employeeId : getEmployeeDetails?.payload?.employeeId;

      let res;
      if (futureCycleLeavesFiling) {
        res = await getTotalLeaveCountV2(
          countEmployeeId,
          addLeaveRequest?.leaveTypeId,
          startDate,
          endDate,
          excludeRequestId
        );
      } else if (anniversaryBasedTimeOff) {
        res = await getTotalLeaveCountV2(
          countEmployeeId,
          addLeaveRequest?.leaveTypeId,
          startDate,
          endDate,
          excludeRequestId
        );
      } else {
        res = await getTotalLeaveCount(
          countEmployeeId,
          addLeaveRequest?.leaveTypeId,
          startDate,
          endDate,
          excludeRequestId
        );
      }

      if (!res?.success) {
        return dispatch(
          showSnackbar({
            dispatch,
            type: "error",
            message: res?.message || "Cannot apply for leave on the selected dates.",
          })
        );
      }

      if (res?.success) {
        setTotalLeaveCount(res?.leaves);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSelectedLeaveDetails = (leaveTypeId) => {
    return employeeLeavesData?.find((leave) => leave?.leaveTypeId === leaveTypeId) || null;
  };

  const getValidDatesForDatePicker = () => {
    if (futureCycleLeavesFiling && selectedLeaveDetails?.nextCycle?.maxFilingDate) {
      return {
        startDate: selectedLeaveDetails.startDate,
        endDate: selectedLeaveDetails.nextCycle.maxFilingDate,
      };
    }
    if (selectedLeaveDetails?.startDate) {
      return { startDate: selectedLeaveDetails.startDate, endDate: selectedLeaveDetails.endDate };
    }
    return null;
  };

  useEffect(() => {
    if (addLeaveRequest?.leaveTypeId) {
      setSelectedLeaveDetails(getSelectedLeaveDetails(addLeaveRequest?.leaveTypeId));
    }
  }, [addLeaveRequest?.leaveTypeId]);

  useEffect(() => {
    fetchTotalLeaveCount();
  }, [addLeaveRequest?.startDate, addLeaveRequest?.endDate, dayDuration, addLeaveRequest?.leaveTypeId]);

  useEffect(() => {
    if (anniversaryBasedTimeOff) {
      setCanUserUploadDocuments(selectedLeaveDetails?.additionalDocumentsRequired);
    } else {
      setCanUserUploadDocuments(selectedLeaveDetails?.attachDocument);
    }
  }, [selectedLeaveDetails]);

  useEffect(() => {
    const options = employeeLeavesData?.map((leave) => ({
      key: leave?.leaveTypeId,
      label: leave?.leaveType,
    }));
    setAddLeaveRequest({
      ...addLeaveRequest,
      leaveTypeId: isEditMode ? leaveToEdit.leaveTypeId : options?.[0]?.key,
    });

    setLeaveTypeDropdownOptions(options);
  }, [employeeLeavesData]);

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { minWidth: "710px", borderRadius: "12px !important" } }}>
      <div className="request-new-leave-title" style={{ paddingLeft: "27px" }}>
        {isEditMode
          ? `Edit ${getEmployeeDetails?.payload?.firstName} ${getEmployeeDetails?.payload?.lastName}'s leave request`
          : `Add a request on ${getEmployeeDetails?.payload?.firstName} ${getEmployeeDetails?.payload?.lastName} behalf`}
      </div>

      <Divider sx={{ border: "1px solid var(--color-primitives-primary-100)", mt: "10px", mb: "13px" }} />
      <Stack direction="row" spacing="26px" alignItems="center">
        <div>
          <div className="leave-taken-select-type" style={{ paddingLeft: "26px", marginTop: "0px" }}>
            Select Type
          </div>
          <div style={{ marginLeft: "26px" }}>
            {/* <CustomDropdown options={years} onSelect={() => {}} buttonLabel="Year:" /> */}
            <CustomDropdown
              options={leaveTypeDropdownOptions}
              disabled={isEditMode}
              style={{ ...dropdownStyles, width: "228px", fontSize: "14px" }}
              onSelect={(key) => {
                setTotalLeaveCount(0);
                setAddLeaveRequest({
                  ...addLeaveRequest,
                  leaveTypeId: key,
                  startDate: moment(),
                  endDate: moment(),
                });
              }}
              buttonLabel={
                leaveTypeDropdownOptions?.find((leave) => leave?.key === addLeaveRequest?.leaveTypeId)?.label || ""
              }
            />
          </div>
        </div>

        <div>
          <div className="leave-taken-select-type" style={{ marginTop: "0px", paddingLeft: "0px" }}>
            Allowed time-off YTD
          </div>

          <div className="allowed-timeoff">
            {anniversaryBasedTimeOff || selectedLeaveDetails?.isFinite
              ? selectedLeaveDetails?.noOfLeaves - selectedLeaveDetails?.scheduled - selectedLeaveDetails?.taken
              : "Unlimited"}
          </div>
        </div>
      </Stack>

      <Divider sx={{ border: "1px solid var(--color-primitives-primary-100)", my: "13px" }} />

      <Stack sx={{ px: "26px" }}>
        <Daterange
          key={`admin-daterange-${addLeaveRequest?.leaveTypeId}-${isEditMode ? leaveToEdit.requestId : "new"}`}
          allowWeekendSelection={!!selectedLeaveDetails?.allowWeekendSelection}
          initialDateRange={editInitialDateRange}
          onChange={(selectedDate) => {
            setAddLeaveRequest({
              ...addLeaveRequest,
              startDate: selectedDate?.startDate,
              endDate: selectedDate?.endDate,
            });
          }}
          leavesCalender={allLeavesAndHolidaysData?.leaves || []}
          holidaysCalender={allLeavesAndHolidaysData?.holidays || []}
          validDates={getValidDatesForDatePicker()}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div className="request-timeoff-indicators-container ml-1" style={{ paddingBottom: "0px" }}>
            <div className="request-timeoff-indicator-box-label">
              <span className="request-timeoff-today-box-indicator" />
              <span className="title-sm">Today</span>
            </div>

            <div className="request-timeoff-indicator-box-label">
              <span className="request-timeoff-selected-box-indicator" />
              <span className="title-sm">Selected</span>
            </div>
          </div>

          <div className="flex gap-05 ml-1 mt-0" style={{ marginBottom: "0px !important" }}>
            <span className="align-self-center calendar-dot-indicator-orange" />

            <p className="body-md align-self-center">Leaves</p>

            <span className="align-self-center calendar-dot-indicator-green" />

            <p className="body-md align-self-center">Holidays</p>
          </div>
        </Stack>
      </Stack>

      <Divider sx={{ border: "1px solid var(--color-primitives-primary-100)", my: "13px" }} />

      {totalLeaveCount === 1 && (
        <Stack spacing="14px" sx={{ pl: "26px", mb: "16px" }}>
          <div className="selected-timeoff-text">Select day duration</div>
          <div className="flex gap-05 my-05">
            <div style={{ width: "160px" }}>
              <RadioButton
                label="Full Day"
                checked={dayDuration === "Full Day"}
                onChange={() => setDayDuration("Full Day")}
                value="full"
                name="dayDuration"
                withContainer
              />
            </div>
            <div style={{ width: "160px" }}>
              <RadioButton
                label="Half Day"
                checked={dayDuration === "Half Day"}
                onChange={() => setDayDuration("Half Day")}
                value="half"
                name="dayDuration"
                withContainer
              />
            </div>
          </div>
        </Stack>
      )}

      <Stack spacing="8px" sx={{ pl: "26px" }}>
        <div className="selected-timeoff-text">Selected</div>
        <div className="req-total-no-of-days">
          {/* {getBusinessDays(addLeaveRequest?.startDate, addLeaveRequest?.endDate)}{" "} */}
          {totalLeaveCount >= 0 ? totalLeaveCount : ""}{" "}
          {leaveTypeDropdownOptions?.find((leave) => leave?.key === addLeaveRequest?.leaveTypeId)?.label || ""} leaves
          (Excluding weekends & holidays){" "}
        </div>
      </Stack>

      <Stack direction="row" spacing="13px" sx={{ pl: "26px", mt: "5px" }}>
        <div className="req-leave-date-from">
          {" "}
          {addLeaveRequest?.startDate ? moment(addLeaveRequest?.startDate).format("MMM DD, YYYY") : "-"}{" "}
        </div>
        <ArrowIcons />
        <div className="req-leave-date-from">
          {" "}
          {addLeaveRequest?.endDate ? moment(addLeaveRequest?.endDate).format("MMM DD, YYYY") : "-"}{" "}
        </div>
      </Stack>

      {totalLeaveCount < 1 && (
        <div className="selected-timeoff-text" style={{ color: "red", paddingLeft: "26px", paddingTop: "5px" }}>
          Note :{" "}
          {selectedLeaveDetails?.noOfLeaves - selectedLeaveDetails?.scheduled - selectedLeaveDetails?.taken <= 0
            ? `You have already taken the maximum allowed  ${
                leaveTypeDropdownOptions?.find((leave) => leave?.key === addLeaveRequest?.leaveTypeId)?.label || ""
              } leaves `
            : "You are unable to apply for leave on the selected dates."}
        </div>
      )}

      {anniversaryBasedTimeOff && canUserUploadDocuments && (
        <div className="mt-075 ml-1">
          <AttachDocuments
            employeeId={getEmployeeDetails?.payload?.employeeId}
            showAddDocumentModal={showAddDocumentModal}
            setShowAddDocumentModal={setShowAddDocumentModal}
            leaveDocuments={filesToUpload}
            onAttached={(files) => {
              setFilesToUpload((prevFiles) => [...prevFiles, ...files]);
              setShowAddDocumentModal(false);
            }}
            onDelete={(file) =>
              setFilesToUpload((prevFiles) => prevFiles.filter((f) => f?.fileName !== file?.fileName))
            }
          />
        </div>
      )}

      <Divider sx={{ border: "1px solid var(--color-primitives-primary-100)", mt: "18px", mb: "13px" }} />

      <Stack sx={{ pl: "26px" }} spacing="13px">
        <div className="req-timeoff-reason">Enter note</div>

        <textarea
          // eslint-disable-next-line react/no-unknown-property
          row={3}
          className="req-leave-taken-reson"
          value={addLeaveRequest?.reason}
          onChange={(e) =>
            setAddLeaveRequest({
              ...addLeaveRequest,
              reason: e.target.value,
            })
          }
        />

        <div
          className="selected-timeoff-text"
          style={{ color: "#BA1A1A", marginTop: "7px", marginLeft: "5px", lineHeight: "19px", fontSize: "11px" }}
        >
          Add a reason behind the edits to save changes
        </div>
      </Stack>

      <Stack direction="row" spacing="13px" sx={{ ml: "26px", mt: "26px", mb: "29px" }}>
        <Button
          variant="outlined"
          sx={{
            borderRadius: "42px",
            height: "38px",
            width: "99px",
            border: "1px solid #3D3E5C",
          }}
          onClick={() => closeModal()}
        >
          <Typography variant="h5" color="var(--color-primitives-primary-600)">
            Cancel
          </Typography>
        </Button>

        <Button
          variant="contained"
          disableElevation
          sx={{
            borderRadius: "42px",
            width: isEditMode ? "auto" : "106px",
            minWidth: isEditMode ? "140px" : "106px",
            px: isEditMode ? "24px" : undefined,
            height: "38px",
            whiteSpace: "nowrap",
            background: "var(--temp-color-primitives-primary-800)",
          }}
          onClick={() => addNewLeaveRequest()}
          disabled={
            totalLeaveCount < 1 ||
            !addLeaveRequest?.reason ||
            !(addLeaveRequest?.reason?.trim() !== "") ||
            (!isEditMode &&
              !(selectedLeaveDetails?.noOfLeaves - selectedLeaveDetails?.scheduled - selectedLeaveDetails?.taken > 0) &&
              !anniversaryBasedTimeOff &&
              selectedLeaveDetails?.isFinite)
          }
        >
          <Typography variant="h6" color="white">
            {isEditMode ? "Save Changes" : "Confirm"}
          </Typography>
        </Button>
      </Stack>
    </Dialog>
  );
}

export default EditLeavesTaken;
